// app/src/screens/OAuthComplete.jsx — the OAuth completion page (D-AUTH §2), mirroring
// Quocia-FB's /auth/{google,linkedin}-complete: the /v1 callback 302s here with either
// ?token=<customToken>&returnTo=… or ?error=…. The page signs in with the custom token,
// POSTs /v1/auth/provision (the idempotent scaffold completion — seeds the account
// language from the public uauLocale), then routes to returnTo (the routeUserPostAuth
// translation). Mounted in BOTH the signed-out and signed-in route trees: the sign-in
// flips `authed` mid-flow and the flow must survive the remount (module-level once-guard;
// the async continuation finishes regardless).

import React, { useEffect, useState } from 'react';
import { navigate } from '@common/router.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { publicLocale } from '../lib/locale-sync.jsx';
import { AuthFrame } from './SignIn.jsx';

const handledTokens = new Set();

function sanitizeReturnTo(v) {
  const s = String(v || '');
  return s.startsWith('/') && !s.startsWith('//') && !s.includes('\\') ? s : '/';
}

export default function OAuthComplete({ provider }) {
  const { t } = useI18n();
  const { auth, api } = useSession();
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const err = q.get('error');
    const token = q.get('token');
    const returnTo = sanitizeReturnTo(q.get('returnTo'));
    if (err) { setError(q.get('detail') || err); return; }
    if (!token) { setError(t('oauthComplete.missingToken')); return; }
    // scrub the custom token from the URL + history BEFORE anything async — it must never
    // linger in the address bar / back-button history (a Firebase custom token is
    // exchangeable until ~1h; a replay from history would mint a session). replaceState,
    // not push, so there's no ?token= entry to return to.
    if (typeof history !== 'undefined' && history.replaceState) {
      history.replaceState(null, '', `/auth/${provider}-complete`);
    }
    if (handledTokens.has(token)) return; // the authed-flip remount / StrictMode re-run
    handledTokens.add(token);
    (async () => {
      try {
        await auth.signInWithCustomToken(token);
        await api.post('auth/provision', { uauLocale: publicLocale() }); // idempotent (reference contract)
        navigate(returnTo, { replace: true }); // no ?token= entry left in history
      } catch (e) {
        handledTokens.delete(token);
        setError(e && e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthFrame title={t('oauthComplete.title', { provider: provider === 'google' ? 'Google' : 'LinkedIn' })} subtitle="">
      {error ? (
        <>
          <p style={{ color: 'var(--coral)', fontSize: 13, margin: '0 0 14px' }}>{t('oauthComplete.failed')} — {error}</p>
          <button className="q-btn press btn-ghost" style={{ width: '100%' }} onClick={() => navigate('/signin')}>{t('authAction.signIn')}</button>
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>{t('oauthComplete.working')}</p>
      )}
    </AuthFrame>
  );
}
